import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faDoorOpen, faTruck, faArrowLeft, faBoxesPacking } from '@fortawesome/free-solid-svg-icons';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';

const OrderDetails = () => {
  const { ordernumber } = useParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const response = await axios.get(
          `https://afric-fresh-backend.onrender.com/order/${ordernumber}`
          // `http://localhost:3005/order/${ordernumber}`
        );
        const orderData = response.data;
        const statusToStep = {
          'pending payment': 0,
          processed: 1,
          shipped: 2,
          completed: 3,
        };
        const step = statusToStep[orderData.orderStatus] ?? 0;
        setCurrentStep(step);
        setOrder(orderData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching order details:', error);
        setError('Failed to load order details. Please try again later.');
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [ordernumber]);

  const progressWidth = `${(currentStep / 3) * 100}%`;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-[#007D44]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-50 p-8 rounded-xl shadow-lg">
          <p className="text-red-500 text-center font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 max-sm:mt-20">
        <div className="max-w-4xl mx-auto p-6">
          <div className="flex justify-between items-center mb-8">
            <Link to="/order">
              <button className="flex items-center space-x-2 text-gray-600 hover:text-[#007D44] transition-colors duration-300">
                <FontAwesomeIcon icon={faArrowLeft} />
                <span>Back to Orders</span>
              </button>
            </Link>
          </div>

          {/* Order Status Card */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Delivering your order...</h2>
              <p className="text-gray-600 text-lg">Arriving at {order.deliveryTime || ''}</p>
            </div>

            {/* Progress Bar */}
            <div className="relative flex items-center justify-between mb-12">
              <div className="absolute left-0 right-0 h-2 bg-gray-200 rounded-full"></div>
              <div className="absolute left-0 h-2 bg-[#007D44] rounded-full transition-all duration-500" style={{ width: progressWidth }}></div>
              
              {/* Status Icons */}
              {['Order Placed', 'Packaging', 'Out for delivery', 'Delivered'].map((step, index) => (
                <div key={step} className="flex flex-col items-center relative">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    currentStep >= index ? 'bg-[#007D44]' : 'bg-gray-200'
                  } transition-colors duration-300`}>
                    <FontAwesomeIcon
                      icon={[faMapMarkerAlt, faBoxesPacking, faTruck, faDoorOpen][index]}
                      className={`text-xl ${currentStep >= index ? 'text-white' : 'text-gray-400'}`}
                    />
                  </div>
                  <span className={`text-sm mt-2 font-medium ${
                    currentStep >= index ? 'text-[#007D44]' : 'text-gray-400'
                  }`}>
                    {step}
                  </span>
                </div>
              ))}
            </div>

            {/* Dynamic Status Section */}
            <div className="bg-[#f8fdfb] rounded-xl p-8 text-center mb-8">
              <div className="mb-4">
                <FontAwesomeIcon
                  icon={[faMapMarkerAlt, faBoxesPacking, faTruck, faDoorOpen][currentStep]}
                  className="text-[#007D44] text-6xl mb-4 animate-bounce"
                />
              </div>
              <p className="text-[#007D44] text-xl font-semibold">
                {[
                  'Confirming your payment...',
                  'Carefully packing your items...',
                  'Your order is on its way!',
                  'Delivered! Enjoy your purchase!'
                ][currentStep]}
              </p>
            </div>
          </div>

          {/* Delivery Details Card */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Delivery Details</h3>
            <div className="space-y-4">
              {[
                {
                  icon: faMapMarkerAlt,
                  label: 'Delivery Address',
                  value: `${order?.billingAddress?.streetHouseApartment}, ${order?.billingAddress?.city} ${order?.billingAddress?.state}, ${order?.billingAddress?.country}`
                },
                {
                  icon: faDoorOpen,
                  label: 'Apartment',
                  value: order?.billingAddress?.apartment || 'No apartment details'
                },
                {
                  icon: faTruck,
                  label: 'Shipping Method',
                  value: order?.shippingMethod || 'Standard delivery'
                }
              ].map((detail, index) => (
                <div key={index} className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#007D44] bg-opacity-10 rounded-full flex items-center justify-center mr-4">
                    <FontAwesomeIcon icon={detail.icon} className="text-[#007D44] text-xl" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{detail.label}</p>
                    <p className="font-medium text-gray-800">{detail.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary Card */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Order Summary</h3>
            <div className="space-y-4 mb-6">
              {order?.cartItems.map((item, index) => (
                <div key={index} className="flex justify-between items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-300">
                  <div>
                    <p className="font-medium text-gray-800">{item.name}</p>
                    <p className="text-sm text-gray-500">Quantity: {item.quantity}</p>
                  </div>
                  <p className="font-semibold text-[#007D44]">${item.price}</p>
                </div>
              ))}
            </div>
            <div className="border-t pt-4">
              <div className="flex justify-between items-center">
                <span className="text-xl font-bold text-gray-800">Total Paid:</span>
                <span className="text-2xl font-bold text-[#007D44]">${order?.total}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default OrderDetails;